import { ReactNode, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AllocationFragment,
  ExpensableBudgetFragment,
  ExpensablePerDiemFragment,
  ExpenseDetailPageQuery,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import ConfirmationScreen from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/perdiem'
import EditExpenseFooterActions from 'modules/expenses/components/expenses/Detail/_Form/Common/EditExpenseFooterActions'
import SnackBarAlerts from 'modules/expenses/components/expenses/Detail/_Form/Common/SnackBarAlerts'
import { type LocalConcept } from 'modules/expenses/components/expenses/Detail/_newForm/Sections/perDiem/DailyConcepts'
import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import {
  normalizeEffectiveOn,
  useDelete,
  useSendForApproval,
  useUpdate,
  useUpdateAndApprove,
} from 'modules/expenses/hooks/useExpensable'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import useNewExpensesPage from 'modules/expenses/hooks/useNewExpensesPage'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import {
  isApprovedExpensable,
  isDraftExpensable,
  isManualExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

import NewPerDiemForm from './new'
import { getPerDiemFormDefaultValues } from './new/form/form'
import { PerDiemSchema } from './new/form/schema'

interface PerDiemFormProps {
  expensable: Expensable
  perDiem: ExpensablePerDiemFragment
  customizationFields: CustomizationField[]
  editable: boolean
  approvalsInformation?: ReactNode
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  canOverrideApprovalFlow: boolean
  canReviewExpensable: boolean
  canSetExpensableToPaid: boolean
  canSeeEmployeeLegalEntity: boolean
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  canUseSpendingPolicies: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  spendingAlerts?: SpendingAlertFragment[]
  hasProjectsFeature: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  hasWorkflowSpendingPolicies: boolean
  approvalsV2Enabled: boolean
  approvalProcess:
    | ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][0]['node']['approvalProcess']
    | null
}

const PerDiemForm = ({
  expensable,
  perDiem: _perDiem,
  customizationFields,
  editable,
  approvalsInformation,
  onClose,
  hasReportOnBehalfFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  canOverrideApprovalFlow,
  canReviewExpensable,
  canSetExpensableToPaid,
  canSeeEmployeeLegalEntity,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  hideAlert,
  canUseSpendingPolicies,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  hasProjectsFeature,
  registerDropHandler,
  hasWorkflowSpendingPolicies,
  approvalsV2Enabled,
  approvalProcess,
}: PerDiemFormProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { canCreateExpensesForOthers } = useNewExpensesPage()
  const isNewPerDiemWizardEnabled = (_perDiem?.perDiemDestinations?.length ?? 0) > 0

  const bulkActions = useBulkActions()
  const { loading: updating } = useUpdate()
  const { loading: updatingAndApproving } = useUpdateAndApprove()
  const { loading: sendingForApproval } = useSendForApproval()
  const { loading: deleting } = useDelete()
  const submitting = updating || updatingAndApproving || sendingForApproval || deleting

  const isDraft = isDraftExpensable(expensable)

  const isDraftManual = expensable && isManualExpensable(expensable) && isDraft
  const syncableState = expensable?.syncableState?.[0] ?? null

  const { alertMessage, alertType } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: Number(expensable.amount),
    reimbursableAmount: null, // no reimbursable amount on per diems
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    spendingAlerts,
    syncableState,
    hasWorkflowSpendingPolicies,
  })

  const showSnackBar =
    alertMessage.length > 0 &&
    !hideAlert &&
    canUseSpendingPolicies &&
    expensable &&
    (isDraft || isPendingExpensable(expensable) || isApprovedExpensable(expensable))

  const defaultValues = getPerDiemFormDefaultValues({
    employee: currentEmployee,
    customizationFields,
    groupId: expensable?.groupId,
    expensable,
    isNewPerDiemWizardEnabled,
  })

  const [formEmployeeId, setFormEmployeeId] = useState<number | null | undefined>(
    defaultValues.employee?.id
  )
  const [hasModifiedConcepts, setHasModifiedConcepts] = useState(false)
  const [modifiedConceptsData, setModifiedConceptsData] = useState<LocalConcept[] | null>(null)

  const { employee } = useCurrentSession()
  const employeeId = formEmployeeId ?? employee?.id
  // TODO: Implement the effective on change based on per diem

  const effectiveOn = new Date().toISOString() // TBD, Dummy data

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(effectiveOn),
    employeeId: employeeId,
    skip: false,
  })

  const perDiemForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      PerDiemSchema({
        customizationFields,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        resourceType: 'perdiem',
        isNewPerDiemWizardEnabled,
      })
    ),
    defaultValues: defaultValues,
  })

  const { trigger } = perDiemForm
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false)
  const [editingFromConfirmation, setEditingFromConfirmation] = useState(false)
  const [scrollTo, setScrollTo] = useState<string | undefined>(undefined)

  // Reset modified concepts state when expensable changes
  useUnsafeEffect(() => {
    setHasModifiedConcepts(false)
    setModifiedConceptsData(null)
  }, [expensable?.id])

  useUnsafeEffect(() => {
    trigger().then((validationResult) => {
      if (editingFromConfirmation) {
        setShowConfirmationScreen(false)
        return
      }
      if (validationResult && !isNewPerDiemWizardEnabled) {
        setShowConfirmationScreen(true)
      }
    })
  }, [customizationFields, trigger, editingFromConfirmation, isNewPerDiemWizardEnabled])

  return (
    <Form padding="s0" notRounded>
      {
        <Box gap="s32" paddingX="s24">
          <FormProvider {...perDiemForm}>
            {showConfirmationScreen ? (
              <ConfirmationScreen
                expensable={expensable}
                customizationFields={customizationFields}
                resourceType="perdiem"
                onEdit={(scrollToSection?: string | undefined) => {
                  setShowConfirmationScreen(false)
                  setEditingFromConfirmation(true)
                  setScrollTo(scrollToSection)
                }}
                editable={editable}
                isNewPerDiemWizardEnabled={isNewPerDiemWizardEnabled}
              />
            ) : (
              <NewPerDiemForm
                customizationFields={customizationFields}
                canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                canCreateExpensesForOthers={canCreateExpensesForOthers}
                canEditExpensesWithPendingStatus={editable}
                expensable={expensable}
                hasProjectsFeature={hasProjectsFeature}
                hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                taxAgencyOrganization={null}
                projectWorkers={newProjectWorkers}
                budgetOptions={newBudgetOptions}
                loadingProjectWorkers={newLoadingProjectWorkers}
                loadingBudgetOptions={newLoadingBudgetOptions}
                onEmployeeIdChange={setFormEmployeeId}
                onEffectiveOnChange={() => {}}
                registerDropHandler={registerDropHandler}
                scrollTo={scrollTo}
                isNewPerDiemWizardEnabled={isNewPerDiemWizardEnabled}
                onConceptsModified={(concepts) => {
                  setHasModifiedConcepts(true)
                  setModifiedConceptsData(concepts)
                }}
              />
            )}
          </FormProvider>

          {!submitting && !bulkActions.submitting && !isDraft && approvalsInformation}
        </Box>
      }
      <SnackBarAlerts
        showSnackBar={showSnackBar}
        alertType={alertType}
        alertMessage={alertMessage}
        toggleAlert={toggleAlert}
        onClickLink={onClickSnackBar}
      />
      <EditExpenseFooterActions
        formType="perdiem"
        expensable={expensable}
        formValues={perDiemForm.getValues()}
        customizationFields={customizationFields}
        canEdit={editable}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        canReviewExpensable={canReviewExpensable}
        canDeleteExpensable={canDeleteExpensable}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        taxAgencyOrganization={null}
        formValid={perDiemForm.formState.isValid}
        formDirty={perDiemForm.formState.isDirty || hasModifiedConcepts}
        trigger={perDiemForm.trigger}
        onClose={onClose}
        approvalsV2Enabled={approvalsV2Enabled}
        approvalProcess={approvalProcess}
        isNewPerDiemWizardEnabled={isNewPerDiemWizardEnabled}
        modifiedConcepts={modifiedConceptsData}
      />
    </Form>
  )
}
export default observer(PerDiemForm)
